import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RTooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, FunnelChart, Funnel, LabelList,
  LineChart, Line, Legend as RLegend,
} from "recharts";
import { Users, UserCheck, TrendingDown, TrendingUp, FileWarning, CalendarClock, ArrowRight, Sparkles, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import DailyHRWork from "@/components/DailyHRWork";
import { PeriodFilter, HRMultiFilter, useHROptions } from "@/components/PeriodFilter";

const RAG_COLORS = { green: "#059669", amber: "#C9A24B", red: "#DC2626" };
const CHART_PALETTE = ["#059669", "#C9A24B", "#0F172A", "#DC2626", "#64748B", "#10B981", "#E5C989", "#1E293B"];

function KpiCard({ label, value, hint, icon: Icon, tone = "navy", testid }) {
  const toneMap = {
    navy: "bg-[var(--yc-navy)] text-white",
    emerald: "bg-[var(--yc-emerald)] text-white",
    gold: "bg-[var(--yc-gold)] text-[var(--yc-navy)]",
    rose: "bg-[var(--yc-rose)] text-white",
    slate: "bg-slate-100 text-[var(--yc-navy)]",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 hover:border-slate-300 transition-colors" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="label-eyebrow">{label}</div>
        <div className={`h-8 w-8 rounded-md flex items-center justify-center ${toneMap[tone]}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="mt-4 font-heading font-bold text-3xl text-[var(--yc-navy)] tracking-tight">{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

function ScorecardRow({ item }) {
  const badge = { green: "rag-green", amber: "rag-amber", red: "rag-red" }[item.rag];
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0" data-testid={`scorecard-row-${item.metric.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="flex-1">
        <div className="text-sm font-medium text-[var(--yc-navy)]">{item.metric}</div>
        <div className="text-xs text-slate-500">Target {item.target}{item.unit}</div>
      </div>
      <div className="flex items-center gap-4">
        <div className="text-right">
          <div className="font-heading font-bold text-lg text-[var(--yc-navy)]">
            {item.actual}<span className="text-xs text-slate-500 ml-0.5">{item.unit}</span>
          </div>
        </div>
        <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold ${badge}`}>
          {item.rag === "green" ? "On Target" : item.rag === "amber" ? "Watch" : "At Risk"}
        </span>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, branch } = useAuth();
  const [kpi, setKpi] = useState(null);
  const [charts, setCharts] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [trend, setTrend] = useState([]);
  const [expiring, setExpiring] = useState([]);
  // Global dashboard filters — drive KPI cards, charts, Daily HR Work and HR-wise table together
  const [preset, setPreset] = useState("all");
  const [range, setRange] = useState({ from: "", to: "" });
  const [hrSel, setHrSel] = useState([]);
  const canSeeHR = ["super_admin", "hr_manager", "branch_manager"].includes(user?.role);
  const hrUsers = useHROptions(canSeeHR);

  useEffect(() => {
    const p = new URLSearchParams();
    if (branch && branch !== "All") p.set("branch", branch);
    if (range.from) p.set("date_from", range.from);
    if (range.to) p.set("date_to", range.to);
    const cp = new URLSearchParams(p);
    if (hrSel.length) cp.set("hr", hrSel.join(","));
    const qs = p.toString() ? `?${p}` : "";
    const cqs = cp.toString() ? `?${cp}` : "";
    (async () => {
      try {
        const [k, c, w, t, e] = await Promise.all([
          api.get(`/dashboard/kpis${qs}`),
          api.get(`/dashboard/charts${cqs}`),
          api.get("/reviews/watchlist"),
          api.get(`/dashboard/attrition-trend?months=12${branch && branch !== "All" ? `&branch=${encodeURIComponent(branch)}` : ""}`),
          api.get("/documents/expiring?days=60").catch(() => ({ data: { items: [] } })),
        ]);
        setKpi(k.data);
        setCharts(c.data);
        setWatchlist(w.data);
        setTrend(t.data.series || []);
        setExpiring(e.data.items || []);
      } catch (err) {
        console.error(err);
      }
    })();
  }, [branch, range, hrSel]);

  if (!kpi || !charts) {
    return <div className="text-slate-500 label-eyebrow">Loading KPIs…</div>;
  }

  const tc = kpi.top_cards;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <div className="label-eyebrow">Executive Overview</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[var(--yc-navy)] tracking-tight mt-1">
            Hello, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-slate-600 mt-1 text-sm">
            Live snapshot across {branch === "All" ? "all 4 branches" : `the ${branch} branch`}. Everything below auto-computes from module data.
          </p>
        </div>
        <Link to="/ai" className="hidden sm:flex items-center gap-2 rounded-full bg-[var(--yc-navy)] text-white px-4 py-2 text-sm hover:bg-[var(--yc-navy-soft)] transition-colors" data-testid="ask-ai-cta">
          <Sparkles className="h-4 w-4 text-[var(--yc-gold)]" /> Ask HR Intelligence
        </Link>
      </div>

      {/* Global filters — drive every card, chart and table below */}
      <div className="flex flex-wrap items-end gap-2 bg-white border border-slate-200 rounded-lg px-4 py-3" data-testid="dashboard-filters">
        <PeriodFilter preset={preset} onPreset={setPreset} range={range} onRange={setRange} testId="dash-period-filter" />
        {canSeeHR && <HRMultiFilter value={hrSel} onChange={setHrSel} users={hrUsers} testId="dash-hr-filter" />}
        {(range.from || hrSel.length > 0) && (
          <button
            onClick={() => { setPreset("all"); setRange({ from: "", to: "" }); setHrSel([]); }}
            className="h-9 px-3 rounded-full border border-slate-200 text-xs text-slate-600 hover:bg-slate-50 transition-colors"
            data-testid="reset-filters-button"
          >
            Reset filters
          </button>
        )}
        {(range.from || hrSel.length > 0) && (
          <div className="text-xs text-slate-500 pb-2.5">
            Showing {hrSel.length === 0 ? "All HR" : hrSel.length === 1 ? (hrUsers.find((u) => u.id === hrSel[0])?.name || "1 HR") : `${hrSel.length} HRs`}
            {range.from ? ` · ${range.from} → ${range.to || range.from}` : " · All time"}
          </div>
        )}
      </div>

      {/* Top KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KpiCard label="Active Employees" value={tc.active_employees} icon={Users} tone="navy" testid="kpi-active-employees" />
        <KpiCard label="Open Positions" value={tc.open_positions} icon={UserCheck} tone="gold" testid="kpi-open-positions" />
        <KpiCard label="Attrition YTD" value={`${tc.attrition_rate}%`} hint="Target ≤ 12%" icon={TrendingDown} tone="rose" testid="kpi-attrition" />
        <KpiCard label="Retention Rate" value={`${tc.retention_rate}%`} hint="Target ≥ 88%" icon={TrendingUp} tone="emerald" testid="kpi-retention" />
        <KpiCard label="Docs Pending" value={tc.docs_pending} icon={FileWarning} tone="slate" testid="kpi-docs-pending" />
        <KpiCard label="Need Check-in" value={tc.needing_checkin} icon={CalendarClock} tone="slate" testid="kpi-check-in" />
      </div>

      {/* Daily HR Work — controlled by the global dashboard filters above */}
      <DailyHRWork controlled preset={preset} range={range} hrSel={hrSel} />

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Scorecard */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="label-eyebrow">KPI Scorecard</div>
              <div className="font-heading font-semibold text-[var(--yc-navy)]">Target vs Actual — auto computed</div>
            </div>
            <span className="text-xs text-slate-500">Green = on target · Amber = watch · Red = at risk</span>
          </div>
          <div className="px-6 pb-4">
            {kpi.scorecard.map((s) => <ScorecardRow key={s.metric} item={s} />)}
          </div>
        </div>

        {/* Insights */}
        <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="auto-insights">
          <div className="label-eyebrow">Auto Insights</div>
          <div className="font-heading font-semibold text-[var(--yc-navy)] mb-4">What the data is telling you</div>
          <ul className="space-y-3">
            {charts.insights.length === 0 && (
              <li className="text-sm text-slate-500">No insights available yet. Add more data to unlock analytics.</li>
            )}
            {charts.insights.map((ins, i) => (
              <li key={i} className="flex gap-3 text-sm text-slate-700 leading-relaxed">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[var(--yc-gold)] shrink-0" />
                {ins}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <ChartCard title="Attrition by Reason" subtitle="Rolling window">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={charts.attrition_by_reason} dataKey="value" nameKey="name" outerRadius={90} innerRadius={50} paddingAngle={2}>
                {charts.attrition_by_reason.map((_, i) => (
                  <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
                ))}
              </Pie>
              <RTooltip />
            </PieChart>
          </ResponsiveContainer>
          <Legend items={charts.attrition_by_reason} />
        </ChartCard>

        <ChartCard title="Attrition by Department">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={charts.attrition_by_department}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
              <RTooltip />
              <Bar dataKey="value" fill="#DC2626" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Hiring Pipeline Funnel">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={charts.hiring_funnel} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} width={90} />
              <RTooltip />
              <Bar dataKey="value" fill="#059669" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Tenure Cohort Distribution">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={charts.tenure_cohorts}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
              <RTooltip />
              <Bar dataKey="value" fill="#C9A24B" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Attrition trend */}
      <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="attrition-trend-chart">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="label-eyebrow">12-Month Trend</div>
            <div className="font-heading font-semibold text-[var(--yc-navy)]">Attrition — monthly exits with rolling averages</div>
          </div>
          <div className="text-xs text-slate-500 flex gap-4">
            <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-[#DC2626]" /> Monthly exits</span>
            <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-[#C9A24B]" /> 3-mo avg</span>
            <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-[#059669]" /> 6-mo avg</span>
            <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-[#0F172A]" /> 12-mo avg</span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={trend}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} />
            <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
            <RTooltip />
            <Line type="monotone" dataKey="exits" stroke="#DC2626" strokeWidth={2} dot={{ r: 3 }} name="Exits" />
            <Line type="monotone" dataKey="rolling_3" stroke="#C9A24B" strokeWidth={2} strokeDasharray="4 4" dot={false} name="3-mo avg" />
            <Line type="monotone" dataKey="rolling_6" stroke="#059669" strokeWidth={2} strokeDasharray="4 4" dot={false} name="6-mo avg" />
            <Line type="monotone" dataKey="rolling_12" stroke="#0F172A" strokeWidth={2} dot={false} name="12-mo avg" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Expiring documents */}
      {expiring.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="expiring-docs-section">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
            <AlertCircle className="h-4 w-4 text-[var(--yc-gold)]" />
            <div>
              <div className="label-eyebrow">Expiry Reminders</div>
              <div className="font-heading font-semibold text-[var(--yc-navy)]">Documents expiring in the next 60 days</div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100 bg-slate-50">
                  <th className="data-cell">Employee</th>
                  <th className="data-cell">Branch</th>
                  <th className="data-cell">Document</th>
                  <th className="data-cell">Expires</th>
                  <th className="data-cell">Days</th>
                </tr>
              </thead>
              <tbody>
                {expiring.slice(0, 10).map((it) => (
                  <tr key={it.file_id} className="border-b border-slate-100 data-row">
                    <td className="data-cell font-medium text-[var(--yc-navy)]">
                      {it.employee_name ? (
                        <Link to={`/employees/${it.entity_id}`} className="hover:underline">{it.employee_name}</Link>
                      ) : "—"}
                    </td>
                    <td className="data-cell text-slate-600">{it.branch || "—"}</td>
                    <td className="data-cell text-slate-600">{it.doc_type || it.original_filename}</td>
                    <td className="data-cell text-slate-500">{it.expires_at?.slice(0, 10)}</td>
                    <td className="data-cell">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${
                        it.expired ? "rag-red" : it.days_remaining <= 14 ? "rag-red" : it.days_remaining <= 30 ? "rag-amber" : "rag-amber"
                      }`}>
                        {it.expired ? `${Math.abs(it.days_remaining)}d overdue` : `${it.days_remaining}d left`}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Watchlist */}
      <div className="bg-white border border-slate-200 rounded-lg" data-testid="watchlist-section">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Retention Risk Watchlist</div>
            <div className="font-heading font-semibold text-[var(--yc-navy)]">High-risk employees — action required</div>
          </div>
          <Link to="/reviews" className="text-sm text-[var(--yc-emerald)] flex items-center gap-1 hover:underline">
            Manage reviews <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-slate-100">
                <th className="data-cell font-semibold">Employee</th>
                <th className="data-cell font-semibold">Department</th>
                <th className="data-cell font-semibold">Branch</th>
                <th className="data-cell font-semibold">Satisfaction</th>
                <th className="data-cell font-semibold">Last Review</th>
                <th className="data-cell font-semibold">Risk</th>
              </tr>
            </thead>
            <tbody>
              {watchlist.length === 0 && (
                <tr><td colSpan={6} className="data-cell text-center text-slate-500 py-8">No high-risk employees. Great news.</td></tr>
              )}
              {watchlist.map((w) => (
                <tr key={w.employee_id} className="border-b border-slate-100 data-row">
                  <td className="data-cell font-medium text-[var(--yc-navy)]">{w.name}</td>
                  <td className="data-cell text-slate-600">{w.department}</td>
                  <td className="data-cell text-slate-600">{w.branch}</td>
                  <td className="data-cell text-slate-600">{w.satisfaction_status}</td>
                  <td className="data-cell text-slate-500">{w.last_review_date ? w.last_review_date.slice(0, 10) : "—"}</td>
                  <td className="data-cell"><span className="rag-red px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold">High</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="label-eyebrow">{subtitle || "Analytics"}</div>
          <div className="font-heading font-semibold text-[var(--yc-navy)]">{title}</div>
        </div>
      </div>
      {children}
    </div>
  );
}

function Legend({ items }) {
  return (
    <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-600">
      {items.map((it, i) => (
        <div key={it.name} className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ background: CHART_PALETTE[i % CHART_PALETTE.length] }} />
          {it.name} <span className="text-slate-400">({it.value})</span>
        </div>
      ))}
    </div>
  );
}
